/**
 * CoverPhotoEditor
 *
 * Drop-in component that replaces the plain file input for cover photos.
 * After the user selects an image it shows a drag-to-reposition UI:
 *
 *   ┌────────────────────────────────┐
 *   │   [image fills the frame]      │  ← user drags up/down
 *   │   ☰  Drag to reposition        │
 *   └────────────────────────────────┘
 *   [ Cancel ]               [ Done  ]
 *
 * Props:
 *   currentUrl      – existing cover photo URL (string | null)
 *   currentPosition – existing Y% (0-100, default 50)
 *   height          – frame height in px (default 140)
 *   buttonLabel     – text for the trigger button
 *   buttonClassName – CSS class for the trigger button
 *   loading         – show spinner on trigger button
 *   onSave(file, yPercent) – called when user confirms
 *
 * The parent is responsible for uploading `file` and persisting `yPercent`.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, GripHorizontal, Check, X } from 'lucide-react';
import { useTranslation } from '../i18n';
import './CoverPhotoEditor.css';

export default function CoverPhotoEditor({
  currentUrl,
  currentPosition = 50,
  height = 140,
  buttonLabel,
  buttonClassName = 'cover-edit-btn',
  loading = false,
  onSave,
}) {
  const { t } = useTranslation('common');
  const fileRef     = useRef();
  const containerRef= useRef();
  const imgRef      = useRef();

  // Editor state
  const [editing,    setEditing]    = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [file,       setFile]       = useState(null);
  const [yPercent,   setYPercent]   = useState(currentPosition);

  // Drag state (refs to avoid stale closures in event listeners)
  const dragging    = useRef(false);
  const dragStartY  = useRef(0);
  const dragStartPct= useRef(0);

  // Update yPercent when prop changes (e.g. after parent reloads)
  useEffect(() => { setYPercent(currentPosition); }, [currentPosition]);

  function handleFileSelect(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
    setYPercent(50); // reset to center for new image
    setEditing(true);
    e.target.value = '';
  }

  function getClientY(e) {
    return e.touches ? e.touches[0].clientY : e.clientY;
  }

  const onPointerDown = useCallback((e) => {
    dragging.current    = true;
    dragStartY.current  = getClientY(e);
    dragStartPct.current= yPercent;
    e.preventDefault();
  }, [yPercent]);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current || !containerRef.current) return;
    const frameH = containerRef.current.offsetHeight;
    const dy     = getClientY(e) - dragStartY.current;
    // Moving finger UP should scroll image DOWN (show lower portion), so invert
    const deltaPct = (dy / frameH) * -100;
    const next   = Math.max(0, Math.min(100, dragStartPct.current + deltaPct));
    setYPercent(next);
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  // Attach/detach global move+up listeners only while editing
  useEffect(() => {
    if (!editing) return;
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup',   onPointerUp);
    window.addEventListener('touchmove', onPointerMove, { passive: true });
    window.addEventListener('touchend',  onPointerUp);
    return () => {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup',   onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend',  onPointerUp);
    };
  }, [editing, onPointerMove, onPointerUp]);

  function handleConfirm() {
    // file is null when repositioning an existing image — parent skips re-upload
    onSave(file, Math.round(yPercent));
    setEditing(false);
  }

  function handleCancel() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setYPercent(currentPosition);
    setEditing(false);
  }

  function startReposition() {
    // Enter drag mode without a new file — use existing image
    setFile(null);
    setPreviewUrl(null);
    setEditing(true);
  }

  const displayUrl     = editing ? (previewUrl || currentUrl) : currentUrl;
  const displayYPct    = `${yPercent}%`;
  const objectPosition = `center ${displayYPct}`;

  return (
    <div className="cpe-root">
      {/* The cover frame */}
      <div
        ref={containerRef}
        className={`cpe-frame${editing ? ' cpe-editing' : ''}`}
        style={{ height }}
        onMouseDown={editing ? onPointerDown : undefined}
        onTouchStart={editing ? onPointerDown : undefined}
      >
        {displayUrl ? (
          <img
            ref={imgRef}
            src={displayUrl}
            alt="Cover"
            className="cpe-img"
            style={{ objectPosition }}
            draggable={false}
          />
        ) : (
          <div className="cpe-placeholder" style={{ height }}/>
        )}

        {editing && (
          <div className="cpe-drag-hint">
            <GripHorizontal size={14}/>
            <span>{t('dragToReposition')}</span>
          </div>
        )}

        {!editing && (
          <>
            <button
              className={`cpe-upload-btn ${buttonClassName}`}
              onClick={() => fileRef.current.click()}
              disabled={loading}
            >
              {loading
                ? <span className="spinner" style={{width:13,height:13,borderWidth:1.5}}/>
                : <><Camera size={13}/> {buttonLabel}</>}
            </button>
            {currentUrl && !loading && (
              <button className="cpe-reposition-btn" onClick={startReposition}>
                <GripHorizontal size={13}/> {t('reposition')}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </>
        )}
      </div>

      {/* Confirm / cancel bar — shown below the frame while editing */}
      {editing && (
        <div className="cpe-toolbar">
          <button className="cpe-cancel-btn" onClick={handleCancel}>
            <X size={14}/> {t('cancel')}
          </button>
          <span className="cpe-hint-text">{t('dragImageHint')}</span>
          <button className="cpe-confirm-btn" onClick={handleConfirm}>
            <Check size={14}/> {t('done')}
          </button>
        </div>
      )}
    </div>
  );
}
